'use client'

import React, { useMemo, useState } from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkspaceElement, ImageData, MireConfig, LEDPanel, MireConnection, MirePowerConnection } from '@/types/synoptique'
import { PORT_COLORS } from '../constants'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronLeft, Download, Grid3X3, Workflow } from 'lucide-react'
import { toast } from 'sonner'

interface SynopticMireItem {
  id: string
  name: string
  imageData: string
  width: number
  height: number
  columns: number
  rows: number
  linkedPanelName?: string
}

interface MireDownloadCardProps {
  dataPages: { workspaceElements?: WorkspaceElement[]; mireConnections?: MireConnection[] }[]
  powerPages: { workspaceElements?: WorkspaceElement[]; mirePowerConnections?: MirePowerConnection[] }[]
  projectName: string
  isTauri: boolean
  // Redondance : backupPorts[processorId][portPrincipal] = portBackup
  backupPorts: Record<string, Record<number, number>>
  setIsLeftPanelOpen: (v: boolean) => void
}

type MireConfigFull = MireConfig & {
  width?: number
  height?: number
  totalResolution?: { width: number; height: number }
}

const sanitizeFilenamePart = (value: string): string =>
  value.trim().replace(/[^a-zA-Z0-9À-ɏ -]/g, '').replace(/\s+/g, '_')

const dataUrlToBytes = (dataUrl: string): Uint8Array => {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

interface CablingConn {
  chainKey: string
  row: number
  col: number
  order: number
  portNum: number
}

const portColor = (n: number) => PORT_COLORS[(n - 1) % PORT_COLORS.length]

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

// Rendu PNG de la mire + schéma de câblage (fonds de cellules, flèches,
// badges P/R ou PC, numéros d'ordre) — même logique visuelle que l'export SVG.
async function renderMireCablingPng(
  mire: SynopticMireItem,
  conns: CablingConn[],
  backupPorts: Record<string, Record<number, number>>,
  domain: 'data' | 'power',
): Promise<Blob | null> {
  const src = mire.imageData.startsWith('data:') ? mire.imageData : `data:image/png;base64,${mire.imageData}`
  const img = await loadImageElement(src)
  const W = img.naturalWidth || mire.width
  const H = img.naturalHeight || mire.height
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.drawImage(img, 0, 0, W, H)

  const cellW = W / mire.columns
  const cellH = H / mire.rows
  const avgCell = (cellW + cellH) / 2
  const strokeW = Math.max(2, Math.min(10, avgCell * 0.05))
  const fontOrder = Math.max(10, Math.min(28, cellW * 0.27))
  const fontBadge = Math.max(10, Math.min(24, cellW * 0.3))
  const center = (row: number, col: number) => ({
    x: (col - 1) * cellW + cellW / 2,
    y: (row - 1) * cellH + cellH / 2,
  })

  // Groupes par chaîne (processeur+port / armoire+sortie), triés par order
  const groups = new Map<string, CablingConn[]>()
  for (const c of conns) {
    const g = groups.get(c.chainKey) ?? []
    g.push(c)
    groups.set(c.chainKey, g)
  }
  const chains = [...groups.values()].map(g => [...g].sort((a, b) => a.order - b.order))

  // 1) Fonds colorés des cellules connectées
  for (const chain of chains) {
    const last = chain[chain.length - 1]
    for (const c of chain) {
      const isFirst = c === chain[0]
      const isLast = c === last
      const hasBackup = domain === 'data' && isLast &&
        backupPorts[chainBackupKey(c.chainKey)]?.[c.portNum] !== undefined
      ctx.fillStyle = portColor(c.portNum)
      ctx.globalAlpha = isFirst || hasBackup ? 1 : 0.25
      ctx.fillRect((c.col - 1) * cellW, (c.row - 1) * cellH, cellW, cellH)
      ctx.globalAlpha = 0.6
      ctx.strokeStyle = portColor(c.portNum)
      ctx.lineWidth = Math.max(1, strokeW * 0.4)
      ctx.strokeRect((c.col - 1) * cellW, (c.row - 1) * cellH, cellW, cellH)
      ctx.globalAlpha = 1
    }
  }

  // 2) Câbles entre cellules consécutives (flèche sur ordres pairs, comme workspace)
  for (const chain of chains) {
    const color = portColor(chain[0].portNum)
    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.lineWidth = strokeW
    ctx.lineCap = 'round'
    for (let i = 1; i < chain.length; i++) {
      const from = center(chain[i - 1].row, chain[i - 1].col)
      const to = center(chain[i].row, chain[i].col)
      const angle = Math.atan2(to.y - from.y, to.x - from.x)
      const arrowLen = strokeW * 4
      const ex = chain[i].order % 2 === 0 ? to.x - arrowLen * Math.cos(angle) : to.x
      const ey = chain[i].order % 2 === 0 ? to.y - arrowLen * Math.sin(angle) : to.y
      ctx.beginPath()
      ctx.moveTo(from.x, from.y)
      ctx.lineTo(ex, ey)
      ctx.stroke()
      if (chain[i].order % 2 === 0) {
        const a = Math.PI / 8
        ctx.beginPath()
        ctx.moveTo(to.x, to.y)
        ctx.lineTo(to.x - arrowLen * Math.cos(angle - a), to.y - arrowLen * Math.sin(angle - a))
        ctx.lineTo(to.x - arrowLen * Math.cos(angle + a), to.y - arrowLen * Math.sin(angle + a))
        ctx.closePath()
        ctx.fill()
      }
    }
  }

  // 3) Labels : P{port}/PC{outlet} sur 1re cellule, R{backup} sur dernière,
  //    numéro d'ordre dans le coin le moins encombré par les câbles
  ctx.textBaseline = 'middle'
  for (const chain of chains) {
    const last = chain[chain.length - 1]
    for (let i = 0; i < chain.length; i++) {
      const c = chain[i]
      const cc = center(c.row, c.col)
      const isFirst = i === 0
      const isLast = c === last

      if (isFirst) {
        ctx.font = `bold ${fontBadge}px Arial, sans-serif`
        ctx.fillStyle = 'white'
        ctx.textAlign = 'center'
        ctx.fillText(domain === 'power' ? `PC${c.portNum}` : `P${c.portNum}`, cc.x, cc.y)
      } else {
        // Coin libre selon les directions des voisins de chaîne
        const dirs = { top: false, bottom: false, left: false, right: false }
        for (const n of [chain[i - 1], chain[i + 1]].filter(Boolean)) {
          const nc = center(n.row, n.col)
          const dx = nc.x - cc.x
          const dy = nc.y - cc.y
          if (Math.abs(dx) > Math.abs(dy)) { dx > 0 ? dirs.right = true : dirs.left = true }
          else { dy > 0 ? dirs.bottom = true : dirs.top = true }
        }
        const x0 = (c.col - 1) * cellW
        const y0 = (c.row - 1) * cellH
        const corners = [
          { blocked: dirs.top || dirs.left, x: x0 + cellW * 0.12, y: y0 + cellH * 0.18 },
          { blocked: dirs.top || dirs.right, x: x0 + cellW * 0.88, y: y0 + cellH * 0.18 },
          { blocked: dirs.bottom || dirs.left, x: x0 + cellW * 0.12, y: y0 + cellH * 0.82 },
          { blocked: dirs.bottom || dirs.right, x: x0 + cellW * 0.88, y: y0 + cellH * 0.82 },
        ]
        const best = corners.find(k => !k.blocked) || corners[0]
        ctx.font = `bold ${fontOrder}px Arial, sans-serif`
        ctx.fillStyle = portColor(c.portNum)
        ctx.textAlign = 'center'
        ctx.shadowColor = 'rgba(0,0,0,0.8)'
        ctx.shadowBlur = fontOrder * 0.25
        ctx.fillText(String(c.order), best.x, best.y)
        ctx.shadowBlur = 0
      }

      if (isLast && domain === 'data') {
        const backup = backupPorts[chainBackupKey(c.chainKey)]?.[c.portNum]
        if (backup !== undefined) {
          ctx.fillStyle = portColor(backup)
          ctx.fillRect((c.col - 1) * cellW, (c.row - 1) * cellH, cellW, cellH)
          ctx.font = `bold ${fontBadge}px Arial, sans-serif`
          ctx.fillStyle = 'white'
          ctx.textAlign = 'center'
          ctx.fillText(`R${backup}`, cc.x, cc.y)
        }
      }
    }
  }

  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
}

// chainKey data : `${processorId}-P${port}` → extrait processorId
function chainBackupKey(chainKey: string): string {
  return chainKey.slice(0, chainKey.lastIndexOf('-P'))
}

export function MireDownloadCard({
  dataPages,
  powerPages,
  projectName,
  isTauri,
  backupPorts,
  setIsLeftPanelOpen,
}: MireDownloadCardProps) {
  const { t } = useLanguage()
  const [selectedMireIds, setSelectedMireIds] = useState<string[]>([])
  const [isDownloading, setIsDownloading] = useState(false)

  // Collecte des mires présentes sur le synoptique (toutes pages Data + Power), dédupliquées
  const mires = useMemo<SynopticMireItem[]>(() => {
    const seen = new Set<string>()
    const items: SynopticMireItem[] = []

    for (const page of [...dataPages, ...powerPages]) {
      for (const el of page.workspaceElements ?? []) {
        if (el.type !== 'image') continue
        const data = el.data as ImageData
        const img = data?.imageData ?? (data?.capturedImage as string | undefined)
        if (!data?.mireConfig || !img) continue

        const cfg = data.mireConfig as MireConfigFull
        const columns = cfg.ledWallMode?.columns ?? 0
        const rows = cfg.ledWallMode?.rows ?? 0
        const width = cfg.width ?? cfg.totalResolution?.width ?? columns * (cfg.ledWallMode?.panelWidth ?? 0)
        const height = cfg.height ?? cfg.totalResolution?.height ?? rows * (cfg.ledWallMode?.panelHeight ?? 0)

        const sourceKey = (data.sourceImageId as string | undefined) ?? img
        const dedupeKey = `${sourceKey}|${width}x${height}|${columns}x${rows}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)

        const linkedPanel = data.linkedPanel as LEDPanel | undefined

        items.push({
          id: el.id,
          name: data.name?.split(' - ')[0] || 'Mire',
          imageData: img,
          width,
          height,
          columns,
          rows,
          linkedPanelName: linkedPanel ? `${linkedPanel.brand} ${linkedPanel.name}` : undefined,
        })
      }
    }
    return items
  }, [dataPages, powerPages])

  // Nommage : NomDuProjet_Resolution_ColonnesxRangées.png (avec suffixe si doublon)
  const mireFilenames = useMemo<Map<string, string>>(() => {
    const used = new Map<string, number>()
    const result = new Map<string, string>()
    const project = sanitizeFilenamePart(projectName) || 'projet'

    for (const mire of mires) {
      const base = `${project}_${mire.width}x${mire.height}_${mire.columns}x${mire.rows}`
      const count = used.get(base) ?? 0
      used.set(base, count + 1)
      result.set(mire.id, count === 0 ? `${base}.png` : `${base}_${count + 1}.png`)
    }
    return result
  }, [mires, projectName])

  const allSelected = mires.length > 0 && selectedMireIds.length === mires.length
  const selectedCount = selectedMireIds.length

  const toggleMire = (id: string, checked: boolean) => {
    setSelectedMireIds(prev => checked ? [...prev, id] : prev.filter(m => m !== id))
  }

  const toggleAll = (checked: boolean) => {
    setSelectedMireIds(checked ? mires.map(m => m.id) : [])
  }

  const downloadMireWeb = (mire: SynopticMireItem) => {
    const link = document.createElement('a')
    link.download = mireFilenames.get(mire.id) || 'mire.png'
    link.href = mire.imageData.startsWith('data:') ? mire.imageData : `data:image/png;base64,${mire.imageData}`
    link.click()
  }

  const downloadMireTauri = async (mire: SynopticMireItem, directory?: string) => {
    const { writeFile } = await import('@tauri-apps/plugin-fs')
    const filename = mireFilenames.get(mire.id) || 'mire.png'

    if (directory) {
      await writeFile(`${directory}/${filename}`, dataUrlToBytes(mire.imageData))
      return true
    }

    const { save } = await import('@tauri-apps/plugin-dialog')
    const filePath = await save({
      filters: [{ name: 'PNG Image', extensions: ['png'] }],
      defaultPath: filename
    })
    if (!filePath) return false
    await writeFile(filePath, dataUrlToBytes(mire.imageData))
    return true
  }

  const downloadSingle = async (mire: SynopticMireItem) => {
    try {
      if (isTauri) {
        const saved = await downloadMireTauri(mire)
        if (!saved) return
      } else {
        downloadMireWeb(mire)
      }
      toast.success(t('synoptic.miresDownload.downloaded').replace('{name}', mireFilenames.get(mire.id) || 'mire.png'))
    } catch {
      toast.error(t('synoptic.miresDownload.error'))
    }
  }

  // Connexions câblées sur cette mire (data : processeur+port, power : armoire+sortie)
  const getCablingConns = (mireId: string, domain: 'data' | 'power'): CablingConn[] => {
    const out: CablingConn[] = []
    if (domain === 'data') {
      for (const p of dataPages) {
        for (const c of p.mireConnections ?? []) {
          if (c.imageId === mireId) {
            out.push({ chainKey: `${c.processorId}-P${c.port}`, row: c.gridPosition.row, col: c.gridPosition.col, order: c.order, portNum: c.port })
          }
        }
      }
    } else {
      for (const p of powerPages) {
        for (const c of p.mirePowerConnections ?? []) {
          if (c.imageId === mireId) {
            out.push({ chainKey: `${c.cabinetId}-O${c.outlet}`, row: c.gridPosition.row, col: c.gridPosition.col, order: c.order, portNum: c.outlet })
          }
        }
      }
    }
    return out
  }

  const mireHasCabling = (mireId: string) =>
    getCablingConns(mireId, 'data').length > 0 || getCablingConns(mireId, 'power').length > 0

  const cabledFilename = (mire: SynopticMireItem, domain: 'data' | 'power') =>
    (mireFilenames.get(mire.id) || 'mire.png').replace(/\.png$/i, '') + `_cablage-${domain}.png`

  const saveBlobWeb = (blob: Blob, filename: string) => {
    const link = document.createElement('a')
    link.download = filename
    link.href = URL.createObjectURL(blob)
    link.click()
    setTimeout(() => URL.revokeObjectURL(link.href), 1000)
  }

  const saveBlobTauri = async (blob: Blob, filename: string, directory?: string) => {
    const { writeFile } = await import('@tauri-apps/plugin-fs')
    const bytes = new Uint8Array(await blob.arrayBuffer())
    if (directory) {
      await writeFile(`${directory}/${filename}`, bytes)
      return true
    }
    const { save } = await import('@tauri-apps/plugin-dialog')
    const filePath = await save({ filters: [{ name: 'PNG Image', extensions: ['png'] }], defaultPath: filename })
    if (!filePath) return false
    await writeFile(filePath, bytes)
    return true
  }

  // Exporte 1 PNG par domaine câblé (data et/ou power)
  const downloadCabled = async (mire: SynopticMireItem, directory?: string) => {
    let exported = 0
    for (const domain of ['data', 'power'] as const) {
      const conns = getCablingConns(mire.id, domain)
      if (conns.length === 0) continue
      const blob = await renderMireCablingPng(mire, conns, backupPorts, domain)
      if (!blob) continue
      const filename = cabledFilename(mire, domain)
      if (isTauri) {
        const saved = await saveBlobTauri(blob, filename, directory)
        if (!saved) continue
      } else {
        saveBlobWeb(blob, filename)
        await new Promise(r => setTimeout(r, 400))
      }
      exported++
    }
    return exported
  }

  const downloadSingleCabled = async (mire: SynopticMireItem) => {
    try {
      const exported = await downloadCabled(mire)
      if (exported === 0) {
        toast.info(t('synoptic.miresDownload.noCabling'))
      } else {
        toast.success(t('synoptic.miresDownload.downloadedCount').replace('{count}', String(exported)))
      }
    } catch {
      toast.error(t('synoptic.miresDownload.error'))
    }
  }

  const downloadSelectionCabled = async () => {
    const selected = mires.filter(m => selectedMireIds.includes(m.id))
    if (selected.length === 0) return
    setIsDownloading(true)
    try {
      let directory: string | undefined
      if (isTauri) {
        const { open } = await import('@tauri-apps/plugin-dialog')
        const dir = await open({ directory: true })
        if (!dir || typeof dir !== 'string') return
        directory = dir
      }
      let exported = 0
      for (const mire of selected) {
        exported += await downloadCabled(mire, directory)
      }
      if (exported === 0) {
        toast.info(t('synoptic.miresDownload.noCabling'))
      } else {
        toast.success(t('synoptic.miresDownload.downloadedCount').replace('{count}', String(exported)))
      }
    } catch {
      toast.error(t('synoptic.miresDownload.error'))
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadSelection = async () => {
    const selected = mires.filter(m => selectedMireIds.includes(m.id))
    if (selected.length === 0) return

    setIsDownloading(true)
    try {
      if (isTauri) {
        const { open } = await import('@tauri-apps/plugin-dialog')
        const directory = await open({ directory: true })
        if (!directory || typeof directory !== 'string') return
        for (const mire of selected) {
          await downloadMireTauri(mire, directory)
        }
      } else {
        for (let i = 0; i < selected.length; i++) {
          downloadMireWeb(selected[i])
          if (i < selected.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 400))
          }
        }
      }
      toast.success(t('synoptic.miresDownload.downloadedCount').replace('{count}', String(selected.length)))
    } catch {
      toast.error(t('synoptic.miresDownload.error'))
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2 pt-3">
        <div
          className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
          onClick={() => setIsLeftPanelOpen(false)}
          title={t('title.clickToHideLeftPanel')}
        >
          <div className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{t('synoptic.miresDownload.title')}</CardTitle>
          </div>
          <ChevronLeft className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
        </div>
        <CardDescription className="text-xs">
          {t('synoptic.miresDownload.desc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 py-3">
        {mires.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">{t('synoptic.miresDownload.empty')}</p>
        ) : (
          <>
            <div className="flex items-center gap-2 pb-1 border-b">
              <Checkbox
                id="mires-select-all"
                checked={allSelected}
                onCheckedChange={(checked) => toggleAll(checked === true)}
              />
              <label htmlFor="mires-select-all" className="text-xs font-medium cursor-pointer">
                {t('synoptic.miresDownload.selectAll')}
              </label>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {mires.map((mire) => {
                const checked = selectedMireIds.includes(mire.id)
                return (
                  <div
                    key={mire.id}
                    className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${checked ? 'bg-accent/50 border-primary/40' : 'bg-background/50'}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(c) => toggleMire(mire.id, c === true)}
                    />
                    <img
                      src={mire.imageData.startsWith('data:') ? mire.imageData : `data:image/png;base64,${mire.imageData}`}
                      alt={mire.name}
                      className="w-14 h-9 object-cover rounded border flex-shrink-0 bg-black"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{mire.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {mire.width}×{mire.height}px · {mire.columns}×{mire.rows} {t('synoptic.miresDownload.panels')}
                      </p>
                      {mire.linkedPanelName && (
                        <p className="text-[10px] text-muted-foreground truncate">{mire.linkedPanelName}</p>
                      )}
                      <p className="text-[9px] text-muted-foreground/70 truncate font-mono">
                        {mireFilenames.get(mire.id)}
                      </p>
                    </div>
                    {mireHasCabling(mire.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 flex-shrink-0"
                        onClick={() => downloadSingleCabled(mire)}
                        title={t('synoptic.miresDownload.downloadCabled')}
                        disabled={isDownloading}
                      >
                        <Workflow className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 flex-shrink-0"
                      onClick={() => downloadSingle(mire)}
                      title={t('synoptic.miresDownload.download')}
                      disabled={isDownloading}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })}
            </div>

            <Button
              onClick={downloadSelection}
              className="w-full"
              size="sm"
              variant="outline"
              disabled={selectedCount === 0 || isDownloading}
            >
              <Download className="h-4 w-4 mr-2" />
              {t('synoptic.miresDownload.downloadSelected').replace('{count}', String(selectedCount))}
            </Button>

            <Button
              onClick={downloadSelectionCabled}
              className="w-full"
              size="sm"
              variant="outline"
              disabled={selectedCount === 0 || isDownloading}
            >
              <Workflow className="h-4 w-4 mr-2" />
              {t('synoptic.miresDownload.downloadSelectedCabled').replace('{count}', String(selectedCount))}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
